import { siteConfig } from "@/lib/config/site"
import { useCart, type CartLine } from "@/features/cart/hooks/use-cart"

// GA4, production only, browser-only. Every event rides Google's consent mode, set up in
// components/google-analytics: a visitor who declined is sent cookieless pings, so what is
// counted here carries no cookie and no identifier. Nothing typed at checkout - name, email,
// phone, address - ever goes in a parameter, as the privacy policy promises.

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const CURRENCY = "INR"
/** Order numbers whose purchase was already reported, newest first. */
const PURCHASES_KEY = "skm.ga.purchases"
/** Tries, 120ms apart, for the inline stub to appear before events are given up on. */
const WAIT_TICKS = 50

const enabled = () => process.env.NODE_ENV === "production" && Boolean(siteConfig.googleAnalyticsId)

const pending: [string, Record<string, unknown>][] = []
let waiting = 0

/**
 * gtag.js reads the dataLayer in order and drops events that come before `config`, so an
 * event raised while the page is still hydrating is held until the inline stub exists.
 */
function flush(): void {
  if (window.gtag) {
    while (pending.length > 0) {
      const [name, params] = pending.shift()!
      window.gtag("event", name, params)
    }
    return
  }
  if (waiting > 0) return
  waiting = WAIT_TICKS
  const timer = window.setInterval(() => {
    waiting -= 1
    if (window.gtag) {
      waiting = 0
      window.clearInterval(timer)
      flush()
    } else if (waiting <= 0) {
      window.clearInterval(timer)
      pending.length = 0
    }
  }, 120)
}

export function gaEvent(name: string, params: Record<string, unknown> = {}): void {
  if (typeof window === "undefined" || !enabled()) return
  pending.push([name, clean(params)])
  flush()
}

/** GA4 counts an empty parameter against the property's limits; undefined ones are dropped. */
function clean<T extends Record<string, unknown>>(params: T): T {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") out[key] = value
  }
  return out as T
}

// gtag.js is 170 KB and waits for the first interaction. A page that must report before anyone
// touches it - the confirmation after a payment redirect - asks for it here instead.
let wanted = false
const wantWaiting = new Set<() => void>()

export function loadAnalyticsNow(): void {
  if (wanted) return
  wanted = true
  const queued = [...wantWaiting]
  wantWaiting.clear()
  for (const run of queued) run()
}

export function onAnalyticsWanted(run: () => void): () => void {
  if (typeof window === "undefined") return () => {}
  if (wanted) {
    run()
    return () => {}
  }
  wantWaiting.add(run)
  return () => void wantWaiting.delete(run)
}

export type GaLine = {
  sku: string
  qty: number
  unitPrice: string | number
  name?: string
  /** The colour, as GA4 reads item_variant. */
  variant?: string
}

/** Item ids are SKUs, as Meta's are, so the two platforms name the same products. */
export function itemsOf(lines: GaLine[]) {
  return lines.map((line, index) =>
    clean({
      item_id: line.sku,
      item_name: line.name,
      item_variant: line.variant,
      price: Number(line.unitPrice),
      quantity: line.qty,
      index,
    }),
  )
}

export function valueOf(lines: GaLine[]): number {
  const total = lines.reduce((sum, line) => sum + Number(line.unitPrice) * line.qty, 0)
  return Math.round(total * 100) / 100
}

function basket(lines: GaLine[]) {
  return { currency: CURRENCY, value: valueOf(lines), items: itemsOf(lines) }
}

export function lineOf(line: CartLine): GaLine {
  return {
    sku: line.sku,
    qty: line.qty,
    unitPrice: line.unitPrice,
    name: line.productName,
    variant: line.colourwayName,
  }
}

export function gaViewItemList(listId: string, listName: string, lines: GaLine[]): void {
  if (lines.length === 0) return
  gaEvent("view_item_list", { item_list_id: listId, item_list_name: listName, ...basket(lines) })
}

export function gaSelectItem(listId: string, listName: string, line: GaLine): void {
  gaEvent("select_item", { item_list_id: listId, item_list_name: listName, ...basket([line]) })
}

export function gaViewItem(line: GaLine): void {
  gaEvent("view_item", basket([line]))
}

export function gaViewCart(lines: GaLine[]): void {
  if (lines.length === 0) return
  gaEvent("view_cart", basket(lines))
  // Google Ads' own cart conversion, imported from GA4 under this name.
  gaEvent("ads_conversion_Shopping_Cart_1", { currency: CURRENCY, value: valueOf(lines) })
}

export function gaBeginCheckout(lines: GaLine[], coupon?: string | null): void {
  if (lines.length === 0) return
  gaEvent("begin_checkout", { ...basket(lines), coupon: coupon ?? undefined })
  gaEvent("ads_conversion_Checkout_1", { currency: CURRENCY, value: valueOf(lines) })
}

export function gaAddShippingInfo(lines: GaLine[], tier: string): void {
  if (lines.length === 0) return
  gaEvent("add_shipping_info", { ...basket(lines), shipping_tier: tier })
}

export function gaAddPaymentInfo(lines: GaLine[], type: string): void {
  if (lines.length === 0) return
  gaEvent("add_payment_info", { ...basket(lines), payment_type: type })
}

export function gaSearch(term: string, results: number): void {
  const search_term = term.trim()
  if (!search_term) return
  gaEvent("search", { search_term })
  gaEvent("view_search_results", { search_term, search_results: results })
}

export function gaFormSubmit(name: string, destination?: string): void {
  gaEvent("form_submit", { form_name: name, form_destination: destination })
}

/** Cashfree's checkout names, kept for the GA4 property that already reports under them. */
export function gaCheckoutStage(name: string, params: Record<string, unknown> = {}): void {
  gaEvent(name, params)
}

function reportedPurchases(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(PURCHASES_KEY) ?? "[]") as unknown
    return Array.isArray(raw) ? raw.filter((n): n is string => typeof n === "string") : []
  } catch {
    return []
  }
}

export type GaOrder = {
  number: string
  total: string
  shipping?: string
  tax?: string
  discount?: string
  coupon?: string | null
  items: GaLine[]
}

/** Once per order per browser: the confirmation page can be reloaded or reopened. */
export function gaPurchase(order: GaOrder): void {
  if (typeof window === "undefined" || !enabled()) return
  const reported = reportedPurchases()
  if (reported.includes(order.number)) return

  gaEvent("purchase", {
    transaction_id: order.number,
    currency: CURRENCY,
    value: Number(order.total),
    shipping: order.shipping === undefined ? undefined : Number(order.shipping),
    tax: order.tax === undefined ? undefined : Number(order.tax),
    coupon: order.coupon ?? undefined,
    items: itemsOf(order.items),
  })
  // Cashfree's names, which this property's funnel is built on.
  gaCheckoutStage("transaction_success", {
    transaction_id: order.number,
    currency: CURRENCY,
    value: Number(order.total),
  })
  gaCheckoutStage("return_to_merchant", { transaction_id: order.number })

  try {
    localStorage.setItem(PURCHASES_KEY, JSON.stringify([order.number, ...reported].slice(0, 20)))
  } catch {
    // Storage blocked: a reload could report it again, nothing worse.
  }
}

/** One add_to_cart or remove_from_cart per change. Not for the cart restored from storage. */
export function watchCartForAnalytics(): () => void {
  let last: Map<string, CartLine> | null = null
  // By line: two picks of one combo share its SKU but are two lines.
  const snapshot = (items: CartLine[]) => new Map(items.map((line) => [line.id, line]))
  const baseline = () => {
    last = snapshot(useCart.getState().items)
  }
  if (useCart.persist.hasHydrated()) baseline()
  const stopHydration = useCart.persist.onFinishHydration(baseline)

  const stopWatching = useCart.subscribe((state) => {
    if (last === null) return
    const before = last
    last = snapshot(state.items)

    const added: GaLine[] = []
    for (const line of state.items) {
      const was = before.get(line.id)?.qty ?? 0
      if (line.qty > was) added.push({ ...lineOf(line), qty: line.qty - was })
    }
    if (added.length > 0) gaEvent("add_to_cart", basket(added))

    const removed: GaLine[] = []
    for (const [id, line] of before) {
      const now = last.get(id)?.qty ?? 0
      if (line.qty > now) removed.push({ ...lineOf(line), qty: line.qty - now })
    }
    if (removed.length > 0) gaEvent("remove_from_cart", basket(removed))
  })

  return () => {
    stopWatching()
    stopHydration()
  }
}
